import React, { useCallback } from "react";
import { Overlay } from "@/components/editor/types";
import { RefreshCcw, RotateCw } from "lucide-react";

/**
 * RotateHandle Component
 *
 * A React component that provides rotation functionality for overlay elements.
 * Renders a rotation handle that users can drag to rotate the parent overlay.
 *
 * @component
 * @param {Object} props
 * @param {Overlay} props.overlay - The overlay object to be rotated
 * @param {Function} props.setOverlay - Callback function to update the overlay properties
 * @param {number} [props.scale] - Optional scale factor for the rotation handle
 *
 * @example
 * <RotateHandle
 *   overlay={overlayObject}
 *   setOverlay={(id, updater) => updateOverlay(id, updater)}
 * />
 */
export const RotateHandle: React.FC<{
  overlay: Overlay;
  setOverlay: (
    overlayId: number,
    updater: (overlay: Overlay) => Overlay
  ) => void;
  scale: number;
  type?: "top-left" | "top-right" | "bottom-left" | "bottom-right"
}> = ({ overlay, setOverlay, type="top-right", scale }) => {

  const HANDLE_SIZE = 14  ;

  const size = Math.round(HANDLE_SIZE / scale);
  const dynamicStyles = {
    "top-left": {
      top: `-${size*1.5}px`,
      left: `-${size*1.5}px`,
    },
    "top-right": {
      top: `-${size*1.5}px`,
      right: `-${size*1.5}px`,
    },
    "bottom-left": {
      bottom: `-${size*1.5}px`,
      left: `-${size*1.5}px`,
    },
    "bottom-right": {
      bottom: `-${size*1.5}px`,
      right: `-${size*1.5}px`,
    },
  };


  /**
   * Handles the start of a rotation gesture and sets up event listeners
   * for tracking the rotation movement.
   *
   * @param {React.PointerEvent} e - The pointer event that initiated the rotation
   */
  const startRotating = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation();

      const rect = e.currentTarget.parentElement?.getBoundingClientRect();
      if (!rect) return;

      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const getAngle = (x: number, y: number) => {
        const deltaX = x - centerX;
        const deltaY = y - centerY;
        return Math.atan2(deltaY, deltaX) * (180 / Math.PI);
      };

      const startAngle = getAngle(e.clientX, e.clientY);
      const startRotation = overlay.rotation || 0;

      const onPointerMove = (e: PointerEvent) => {
        const currentAngle = getAngle(e.clientX, e.clientY);
        const deltaAngle = currentAngle - startAngle;

        setOverlay(overlay.id, (o) => ({
          ...o,
          rotation: startRotation + deltaAngle,
          isDragging: true,
        }));
      };

      const onPointerUp = () => {
        window.removeEventListener("pointermove", onPointerMove);
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp, { once: true });
    },
    [overlay, setOverlay]
  );
  return (
    <div
      onPointerDown={startRotating}
      className="opacity-50 hover:opacity-100 transition-opacity duration-200"
      style={{
        position: "absolute",
        width: size,
        height: size,
        cursor: "grab",
        display: "flex",
        alignItems: "center",
        pointerEvents: "all",
        justifyContent: "center",
        color: "#3B8BF2",
        ...dynamicStyles[type],
      }}
    >
      <RefreshCcw size={size} strokeWidth={3} />
    </div>
  );
};
